import { useEffect} from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { monokai } from "react-syntax-highlighter/dist/esm/styles/hljs";

const codeSnippets = {
  "digital-analytics": {
    javascript: String.raw`// Track user behavior in JavaScript
import { IntelTracker } from '@analytics-intel/tracking';

const tracker = new IntelTracker('YOUR_WRITE_KEY');

// Identify user
tracker.identify('user_123', {
  email: 'trader@example.com',
  plan: 'professional',
  risk_profile: 'moderate',
  account_value: 150000
});

// Track page view
tracker.track('page_view', {
  page: '/dashboard',
  referrer: '/pricing',
  session_id: 'sess_xyz'
});

// Track LLM interaction
tracker.track('llm.prompt', {
  model: 'claude-sonnet-4-5',
  intent: 'portfolio_analysis',
  tokens_prompt: 342,
  symbols: ['AAPL', 'GOOGL', 'MSFT'],
  temperature: 0.3
});

// Track symbol research
tracker.track('symbol_researched', {
  symbol: 'NVDA',
  data_points_viewed: ['quote', 'fundamentals', 'news'],
  time_spent_seconds: 185
});`,

    python: String.raw`# Track user events and LLM interactions
from analytics_intel.tracking import IntelTracker

tracker = IntelTracker(write_key="YOUR_WRITE_KEY")

# Identify user
tracker.identify(
    user_id="user_123",
    traits={
        "email": "trader@example.com",
        "plan": "professional",
        "risk_profile": "moderate",
        "account_value": 150000
    }
)

# Track page view
tracker.track("page_view", {
    "page": "/dashboard",
    "referrer": "/pricing",
    "session_id": "sess_xyz"
})

# Track LLM prompt
tracker.track("llm.prompt", {
    "model": "claude-sonnet-4-5",
    "intent": "portfolio_analysis",
    "tokens_prompt": 342,
    "symbols": ["AAPL", "GOOGL", "MSFT"],
    "temperature": 0.3
})

# Track LLM response
tracker.track("llm.response", {
    "model": "claude-sonnet-4-5",
    "tokens_completion": 156,
    "latency_ms": 620,
    "outcome": "answered",
    "satisfaction": "positive",
    "tool_calls": 3
})

# Track symbol research
tracker.track("symbol_researched", {
    "symbol": "NVDA",
    "data_points_viewed": ["quote", "fundamentals", "news"],
    "time_spent_seconds": 185
})`,

    curl: String.raw`# Track LLM interaction via cURL
curl -X POST "https://track.analyticsintelligence.ai/v1/track"
  -H "Authorization: Bearer YOUR_WRITE_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "userId": "user_123",
    "event": "llm.prompt",
    "properties": {
      "model": "claude-sonnet-4-5",
      "intent": "portfolio_analysis",
      "tokens_prompt": 342,
      "symbols": ["AAPL", "GOOGL", "MSFT"],
      "temperature": 0.3
    },
    "timestamp": "2025-10-28T16:30:00Z"
  }'`,
  },

  "capital-markets": {
    javascript: String.raw`// Compare EV stocks with AI
import { IntelAI } from '@analytics-intel/ai';

const client = new IntelAI('YOUR_API_KEY');
const comparison = await client.llmCompare({
  symbols: ['TSLA', 'RIVN', 'LCID'],
  criteria: ['valuation', 'growth', 'profitability'],
  recommendation_type: 'best_value'
});

console.log(\`Recommended: \${comparison.recommended}\`);
comparison.comparisons.forEach(stock => {
  console.log(\`\${stock.symbol}: \${stock.score}/100\`);
});`,

    python: String.raw`# AI-powered comparison of multiple stocks
from analyticsintel import IntelAI

client = IntelAI(api_key="YOUR_API_KEY")
comparison = client.compare({
    "symbols": ["TSLA", "RIVN", "LCID"],
    "criteria": [
        "valuation",
        "growth_potential",
        "profitability",
        "market_position"
    ],
    "recommendation_type": "best_value"
})

print(f"Winner: {comparison['recommended']}")
print(f"\nComparison Summary:\n{comparison['summary']}")

for symbol in comparison["comparisons"]:
    print(f"\n{symbol['symbol']}:")
    print(f"  Score: {symbol['score']}/100")
    print(f"  Pros: {', '.join(symbol['pros'])}")
    print(f"  Cons: {', '.join(symbol['cons'])}")`,
  curl: String.raw`curl -X POST "https://api.analyticsintelligence.ai/v1/llm/compare"
  -H "Authorization: Bearer YOUR_API_KEY" \
  -d '{
    "symbols": ["TSLA", "RIVN", "LCID"],
    "criteria": ["valuation", "growth", "profitability"],
    "recommendation_type": "best_value"
  }'`
  },
};


export default function CodePanel({ activeTab, onTabChange, product }) {
  // const [copied, setCopied] = useState(false);

  // const handleCopy = () => {
  //   const productSnippets =
  //     codeSnippets[product] || codeSnippets["digital-analytics"];
  //   navigator.clipboard.writeText(productSnippets[activeTab]);
  //   setCopied(true);
  //   setTimeout(() => setCopied(false), 2000);
  // };

  const productSnippets =
    codeSnippets[product] || codeSnippets["digital-analytics"];

  useEffect(() => {
    onTabChange("javascript");
    // eslint-disable-next-line
  }, [product]);

  return (
    <div className="border border-[#82868A]/50 rounded-xl backdrop-blur overflow-hidden py-6 px-3 space-y-6">
      {/* Tabs */}
      <div className="flex">
        {Object.keys(productSnippets).map((tab) => (
          <button
            key={tab}
            onClick={() => onTabChange(tab)}
            className={`px-4 py-3 text-sm font-extrabold transition-colors rounded-md ${
              activeTab === tab
                ? "text-white bg-[#EFF0F712]"
                : "text-[#C0C0C0] hover:text-gray-200"
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Code content */}
      <div className="p-6 code-highlighter bg-[#82868A0D] rounded-[4px] h-[500px] overflow-auto border-[0.2px] border-[#82868A]/50">
        <SyntaxHighlighter language="javascript" style={monokai}>
          {productSnippets[activeTab]}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
